import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Send, X, Check, XCircle, Loader2, Lightbulb,
  History, ListChecks, ChevronRight, CheckCircle2, BookOpen,
  ArrowRight, Sparkles,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../../lib/api-client';
import { useSessionStore } from '../../stores/sessionStore';
import { useCopilotStore } from '../../stores/copilotStore';
import { loadCopilotHistory } from '../../features/copilot/copilotHistoryApi';
import { MessageTimestamp } from './MessageTimestamp';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ChatMessage {
  role: 'user' | 'assistant';
  type: 'analysis' | 'reply' | 'user' | 'success' | 'tool_call' | 'message' | 'error' | 'execution' | 'value_tangibilization';
  content: string;
  created_at?: number | string;
  learned?: string;
  plan?: string[];
  requires_info?: boolean;
  question?: string;
  execution_log?: Array<{
    tool: string;
    args: any;
    status: string;
    result: any;
    error: string | null;
  }>;
  similar_tasks?: any[];
  value_metrics?: {
    timeSavedMinutes?: number;
    financialSavingsBrl?: number;
    formattedSavings?: string;
    slaSpeedup?: string;
    impactHeadline?: string;
    impactDetails?: string;
    extraordinaryBadge?: string;
  };
}

interface GlobalCopilotProps {
  /** Legacy prop — still supported so AppLayout doesn't break */
  isOpen?: boolean;
  onClose?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const BFF = API_URL;

function authHeaders(token?: string | null): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ExecutionBubble({ msg }: { msg: ChatMessage }) {
  const [showDetails, setShowDetails] = useState(false);
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '16px',
      background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.05) 100%)',
      border: '1px solid rgba(16,185,129,0.25)',
      borderRadius: '12px',
      fontSize: '13px',
      lineHeight: '1.6',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '8px',
          background: 'rgba(16,185,129,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#34d399', flexShrink: 0,
        }}>
          <CheckCircle2 size={14} />
        </div>
        <span style={{ fontWeight: 700, color: '#34d399', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Resolução Autônoma Concluída
        </span>
      </div>

      <div style={{ margin: 0, color: 'rgba(255,255,255,0.85)' }}>
        {renderMarkdown(msg.content)}
      </div>

      {msg.execution_log && msg.execution_log.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              alignSelf: 'flex-start',
              background: 'none',
              border: 'none',
              color: '#34d399',
              fontSize: '11px',
              fontWeight: 750,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {showDetails ? 'Ocultar detalhes das ações no banco' : `Ver ${msg.execution_log.length} ação(ões) no banco de dados`}
          </button>

          {showDetails && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              padding: '10px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              {msg.execution_log.map((log, index) => (
                <div key={index} style={{
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  color: log.status === 'ok' ? '#a7f3d0' : '#fca5a5',
                  borderBottom: index < msg.execution_log!.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  paddingBottom: '4px',
                  marginBottom: '4px',
                }}>
                  <div><strong>Action:</strong> {log.tool}</div>
                  <div><strong>Arguments:</strong> {JSON.stringify(log.args)}</div>
                  <div><strong>Result:</strong> {typeof log.result === 'object' ? JSON.stringify(log.result) : log.result}</div>
                  {log.error && <div style={{ color: '#ef4444' }}><strong>Error:</strong> {log.error}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalysisBubble({ msg }: { msg: ChatMessage }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '16px',
      background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.06) 100%)',
      border: '1px solid rgba(99,102,241,0.2)',
      borderRadius: '12px',
      fontSize: '13px',
      lineHeight: '1.6',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '8px',
          background: 'rgba(99,102,241,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#818cf8', flexShrink: 0,
        }}>
          <Sparkles size={14} />
        </div>
        <span style={{ fontWeight: 700, color: '#a5b4fc', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Análise do Copilot
        </span>
      </div>

      {/* Analysis text */}
      <div style={{ margin: 0, color: 'rgba(255,255,255,0.85)' }}>
        {renderMarkdown(msg.content)}
      </div>

      {/* Learned from history */}
      {msg.learned && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '10px 12px',
          background: 'rgba(16,185,129,0.06)',
          border: '1px solid rgba(16,185,129,0.15)',
          borderRadius: '8px',
        }}>
          <History size={13} style={{ color: '#34d399', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
            <strong style={{ color: '#34d399' }}>Histórico: </strong>{msg.learned}
          </p>
        </div>
      )}

      {/* Action plan */}
      {msg.plan && msg.plan.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <ListChecks size={13} style={{ color: '#818cf8' }} />
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Plano de Resolução
            </span>
          </div>
          {msg.plan.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{
                minWidth: '20px', height: '20px', borderRadius: '50%',
                background: 'rgba(99,102,241,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: 700, color: '#818cf8', flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{step}</span>
            </div>
          ))}
        </div>
      )}

      {/* Question if requires info */}
      {msg.requires_info && msg.question && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '10px 12px',
          background: 'rgba(245,158,11,0.06)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: '8px',
        }}>
          <Lightbulb size={13} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
            <strong style={{ color: '#f59e0b' }}>Preciso de mais informações: </strong>{msg.question}
          </p>
        </div>
      )}
    </div>
  );
}

// Simple, robust Markdown parser helper for rendering tables, bolding, lists and newlines beautifully in inline CSS
function renderMarkdown(text: string): React.ReactNode {
  if (!text) return null;

  // Split content by table blocks or standard blocks
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let tableRows: string[][] = [];
  let inTable = false;

  const parseInline = (str: string) => {
    // Bold matching (**text**)
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} style={{ color: '#fff', fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
      }
      // Inline code matching (`code`)
      const codeParts = part.split(/(`[^`]+`)/g);
      return codeParts.map((subPart, subIdx) => {
        if (subPart.startsWith('`') && subPart.endsWith('`')) {
          return (
            <code key={`${idx}-${subIdx}`} style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '2px 4px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '11px',
              color: '#f43f5e'
            }}>
              {subPart.slice(1, -1)}
            </code>
          );
        }
        return subPart;
      });
    });
  };

  const renderTable = (rows: string[][], tableIdx: number) => {
    if (rows.length === 0) return null;
    const hasHeader = rows.length > 1 && rows[1].every(cell => cell.trim().startsWith('-'));
    const headerRow = hasHeader ? rows[0] : null;
    const bodyRows = hasHeader ? rows.slice(2) : rows;

    return (
      <div key={`table-${tableIdx}`} style={{ overflowX: 'auto', margin: '12px 0', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
          {headerRow && (
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {headerRow.map((cell, idx) => (
                  <th key={idx} style={{ padding: '8px 10px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{parseInline(cell.trim())}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows.map((row, rIdx) => (
              <tr key={rIdx} style={{ borderBottom: rIdx < bodyRows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', background: rIdx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                {row.map((cell, cIdx) => (
                  <td key={cIdx} style={{ padding: '8px 10px', color: 'rgba(255,255,255,0.75)' }}>{parseInline(cell.trim())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  let elementIdx = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table row matching (| cell | cell |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      inTable = true;
      const cells = line.split('|').slice(1, -1);
      tableRows.push(cells);
      continue;
    } else if (inTable) {
      // End of table block
      elements.push(renderTable(tableRows, elementIdx++));
      tableRows = [];
      inTable = false;
    }

    // Heading matching (### Title)
    if (line.startsWith('### ')) {
      elements.push(<h4 key={elementIdx++} style={{ margin: '14px 0 6px', fontSize: '13px', fontWeight: 700, color: '#fff' }}>{parseInline(line.substring(4))}</h4>);
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(<h3 key={elementIdx++} style={{ margin: '16px 0 8px', fontSize: '14px', fontWeight: 700, color: '#fff' }}>{parseInline(line.substring(3))}</h3>);
      continue;
    }

    // List item matching (- Item or * Item)
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      elements.push(
        <div key={elementIdx++} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', margin: '4px 0', paddingLeft: '8px' }}>
          <span style={{ color: '#818cf8', fontSize: '12px', marginTop: '1px' }}>•</span>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>{parseInline(line.trim().substring(2))}</span>
        </div>
      );
      continue;
    }

    // Paragraph rendering with inline styles
    if (line.trim() !== '') {
      elements.push(<p key={elementIdx++} style={{ margin: '6px 0', color: 'rgba(255,255,255,0.8)', minHeight: '1em' }}>{parseInline(line)}</p>);
    }
  }

  // Handle trailing table if text ends with one
  if (inTable && tableRows.length > 0) {
    elements.push(renderTable(tableRows, elementIdx++));
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{elements}</div>;
}

function ReplyBubble({ msg }: { msg: ChatMessage }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      fontSize: '13px',
      lineHeight: '1.6',
      color: 'rgba(255,255,255,0.85)',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Bot size={14} style={{ color: '#818cf8', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: '#818cf8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Copilot</span>
      </div>
      <div style={{ margin: 0 }}>
        {renderMarkdown(msg.content)}
      </div>
      {msg.plan && msg.plan.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
          {msg.plan.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
              <ChevronRight size={12} style={{ color: '#818cf8', flexShrink: 0, marginTop: '3px' }} />
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>{step}</span>
            </div>
          ))}
        </div>
      )}
      <MessageTimestamp value={msg.created_at} />
    </div>
  );
}

function UserBubble({ msg }: { msg: ChatMessage }) {
  return (
    <div style={{
      alignSelf: 'flex-end',
      maxWidth: '80%',
      padding: '10px 14px',
      background: 'rgba(99,102,241,0.15)',
      border: '1px solid rgba(99,102,241,0.25)',
      borderRadius: '12px 12px 4px 12px',
      fontSize: '13px',
      color: 'rgba(255,255,255,0.9)',
      lineHeight: '1.5',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      {msg.content}
      <MessageTimestamp value={msg.created_at} align="right" />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function GlobalCopilot({ isOpen: legacyIsOpen, onClose: legacyOnClose }: GlobalCopilotProps) {
  const store = useCopilotStore();
  const isOpen = legacyIsOpen !== undefined ? legacyIsOpen : store.isOpen;
  const handleClose = legacyOnClose || store.close;

  const [generalMessage, setGeneralMessage] = useState('');
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalResponse, setGeneralResponse] = useState<any>(null);
  const [generalThread, setGeneralThread] = useState<ChatMessage[]>([]);
  const [historyToken, setHistoryToken] = useState<string | null>(null);

  // Task-mode state
  const [taskThread, setTaskThread] = useState<ChatMessage[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [taskCompleted, setTaskCompleted] = useState(false);

  const token = useSessionStore((state) => state.session?.access_token);
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const activeTask = store.activeTask;
  const isTaskMode = Boolean(activeTask);

  useEffect(() => {
    if (!isOpen || isTaskMode || !token || historyToken === token) return;
    setGeneralLoading(true);
    loadCopilotHistory(token)
      .then((items) => setGeneralThread(items.map((item) => ({
        role: item.role,
        type: item.role === 'user' ? 'user' : 'reply',
        content: item.content,
        created_at: item.created_at,
      }))))
      .catch(() => setGeneralThread([]))
      .finally(() => {
        setHistoryToken(token);
        setGeneralLoading(false);
      });
  }, [historyToken, isOpen, isTaskMode, token]);

  // Load task analysis when a task is selected
  useEffect(() => {
    if (!isOpen || !activeTask) {
      setTaskThread([]);
      setTaskCompleted(false);
      return;
    }

    setTaskLoading(true);
    setTaskThread([]);
    setTaskCompleted(false);

    fetch(`${BFF}/v1/crm/copilot/task-resolve`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({
        task_id: activeTask.id,
        task_title: activeTask.title,
        task_description: activeTask.description || '',
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        const threadItems = [...(data.thread || [])];
        if (data.value_metrics) {
          threadItems.push({
            role: 'assistant',
            type: 'value_tangibilization',
            content: `⚡ EXTRAORDINÁRIO: Tarefa resolvida autonomamente!\n• ⏱️ Economia: ~${data.value_metrics.timeSavedMinutes} min de trabalho manual\n• 💰 Custo Operacional Evitado: ${data.value_metrics.formattedSavings}\n• 🚀 Ganho de SLA: ${data.value_metrics.slaSpeedup}\n• 🎯 Impacto: ${data.value_metrics.impactDetails}`,
            value_metrics: data.value_metrics,
          });
        }
        setTaskThread(threadItems);
      })
      .catch(() => {
        setTaskThread([{
          role: 'assistant',
          type: 'analysis',
          content: `Pronto para ajudar com a tarefa "${activeTask.title}". Como posso ajudar?`,
          plan: [],
        }]);
      })
      .finally(() => setTaskLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTask?.id]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [taskThread, taskLoading, generalThread, generalLoading]);

  // General mode: send message
  const handleGeneralSend = async () => {
    if (!generalMessage.trim()) return;
    const message = generalMessage.trim();
    setGeneralThread((current) => [...current,
      { role: 'user', type: 'user', content: message, created_at: Date.now() }]);
    setGeneralMessage('');
    setGeneralLoading(true);
    setGeneralResponse(null);
    try {
      const res = await fetch(`${BFF}/v1/crm/copilot/intent`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          message,
          context: `URL atual: ${location.pathname}`,
        }),
      });
      const data = await res.json();
      const response = res.ok && !data.error
        ? data : { type: 'error', content: data.error || 'Falha na requisição.' };
      setGeneralResponse(response.type === 'tool_call' ? response : null);
      setGeneralThread((current) => [...current, {
        role: 'assistant', type: response.type === 'error' ? 'error' : 'reply',
        content: response.content || response.message || 'Resposta processada pelo Copilot.',
        created_at: Date.now(),
      }]);
    } catch {
      setGeneralThread((current) => [...current, { role: 'assistant', type: 'error',
        content: 'Ocorreu um erro ao conectar com o CRM Copilot.', created_at: Date.now() }]);
    } finally {
      setGeneralLoading(false);
    }
  };

  // General mode: execute tool call
  const handleExecute = async () => {
    if (!generalResponse || generalResponse.type !== 'tool_call') return;
    setGeneralLoading(true);
    try {
      const res = await fetch(`${BFF}/v1/crm/copilot/execute`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ intent: generalResponse.intent, args: generalResponse.arguments }),
      });
      const data = await res.json();
      setGeneralResponse(
        res.ok && data.success !== false && !data.error
          ? { type: 'success', content: data.message || 'Ação executada com sucesso!' }
          : { type: 'error', content: data.message || data.error || 'Falha ao executar ação.' }
      );
      setGeneralThread((current) => [...current, { role: 'assistant',
        type: res.ok && data.success !== false && !data.error ? 'success' : 'error',
        content: data.message || data.error || 'Ação processada.', created_at: Date.now() }]);
      setGeneralResponse(null);
    } catch {
      setGeneralResponse({ type: 'error', content: 'Falha ao executar ação.' });
    } finally {
      setGeneralLoading(false);
    }
  };

  // Task mode: send user reply
  const handleTaskReply = useCallback(async () => {
    if (!userInput.trim() || !activeTask) return;
    const msg: ChatMessage = { role: 'user', type: 'user', content: userInput.trim() };
    setTaskThread((prev) => [...prev, msg]);
    setUserInput('');
    setTaskLoading(true);

    try {
      const res = await fetch(`${BFF}/v1/crm/copilot/task-session/${activeTask.id}/reply`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          message: msg.content,
          thread: taskThread,
          task_title: activeTask.title,
        }),
      });
      const data = await res.json();
      setTaskThread((prev) => [
        ...prev,
        { role: 'assistant', type: 'reply', content: data.content || 'Sem resposta do agente.', plan: data.plan },
      ]);
    } catch {
      setTaskThread((prev) => [
        ...prev,
        { role: 'assistant', type: 'reply', content: 'Houve um erro ao processar sua resposta. Tente novamente.' },
      ]);
    } finally {
      setTaskLoading(false);
    }
  }, [userInput, activeTask, taskThread, token]);

  // Task mode: mark task as complete
  const handleCompleteTask = async () => {
    if (!activeTask) return;
    setTaskLoading(true);
    try {
      await fetch(`${BFF}/v1/crm/copilot/task-complete`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ task_id: activeTask.id }),
      });
      setTaskCompleted(true);
      setTaskThread((prev) => [
        ...prev,
        { role: 'assistant', type: 'success', content: `✅ Tarefa "${activeTask.title}" marcada como concluída!` },
      ]);
    } catch {
      setTaskThread((prev) => [
        ...prev,
        { role: 'assistant', type: 'error', content: 'Não foi possível marcar a tarefa como concluída. Tente pela lista de tarefas.' },
      ]);
    } finally {
      setTaskLoading(false);
    }
  };

  if (!isOpen) return null;

  // ── Shared panel wrapper ──────────────────────────────────────────────────
  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(4px)',
  };

  const innerStyle: React.CSSProperties = {
    background: 'var(--bg-surface, #1a1a2e)',
    width: '100%',
    maxWidth: isTaskMode ? '680px' : '600px',
    maxHeight: '85vh',
    borderRadius: '16px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  // ── TASK MODE ─────────────────────────────────────────────────────────────
  if (isTaskMode && activeTask) {
    return (
      <div style={panelStyle} onClick={handleClose}>
        <div style={innerStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(99,102,241,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 20px rgba(99,102,241,0.3)',
              }}>
                <Bot size={18} color="#fff" />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: '#f3f4f6' }}>CRM Copilot</p>
                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Modo de Resolução de Tarefa</p>
              </div>
            </div>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '4px' }}>
              <X size={20} />
            </button>
          </div>

          {/* Task context banner */}
          <div style={{
            padding: '12px 20px',
            background: 'rgba(255,255,255,0.02)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <BookOpen size={14} style={{ color: '#818cf8', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
                Tarefa em resolução
              </p>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>{activeTask.title}</p>
              {activeTask.description && (
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{activeTask.description}</p>
              )}
            </div>
          </div>

          {/* Chat thread */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {taskLoading && taskThread.length === 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255,255,255,0.4)', padding: '20px 0' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: '#818cf8' }} />
                <span style={{ fontSize: '13px' }}>Consultando histórico e analisando a tarefa…</span>
              </div>
            )}

            {taskThread.map((msg, i) => {
              if (msg.role === 'user') return <UserBubble key={i} msg={msg} />;
              if (msg.type === 'analysis') return <AnalysisBubble key={i} msg={msg} />;
              if (msg.type === 'execution') return <ExecutionBubble key={i} msg={msg} />;
              if (msg.type === 'value_tangibilization' || msg.value_metrics) {
                const vm = msg.value_metrics || {};
                return (
                  <div key={i} style={{
                    padding: '14px 16px',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(16,185,129,0.12) 100%)',
                    border: '1px solid rgba(16,185,129,0.3)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(16,185,129,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 8px', borderRadius: '6px',
                        background: 'rgba(16,185,129,0.2)', color: '#34d399',
                        fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em'
                      }}>
                        ⚡ EXTRAORDINÁRIO • VALOR TANGIBILIZADO
                      </span>
                      <span style={{ fontSize: '12px', color: '#34d399', fontWeight: 800 }}>
                        {vm.formattedSavings || 'R$ 31,25 economizados'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                      <span style={{ background: 'rgba(255,255,255,0.06)', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                        ⏱️ ~{vm.timeSavedMinutes || 25} min poupados
                      </span>
                      <span style={{ background: 'rgba(255,255,255,0.06)', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                        🚀 SLA instantâneo (&lt; 4s)
                      </span>
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.4', whiteSpace: 'pre-line' }}>
                      {vm.impactDetails || msg.content}
                    </p>
                  </div>
                );
              }
              if (msg.type === 'success') return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '12px 14px',
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.2)',
                  borderRadius: '10px',
                  color: '#34d399',
                  fontSize: '13px',
                }}>
                  <CheckCircle2 size={16} />
                  <span>{msg.content}</span>
                </div>
              );
              if (msg.type === 'error') return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '12px 14px',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: '10px',
                  color: '#f87171',
                  fontSize: '13px',
                }}>
                  <XCircle size={16} />
                  <span>{msg.content}</span>
                </div>
              );
              return <ReplyBubble key={i} msg={msg} />;
            })}

            {taskLoading && taskThread.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.35)', fontSize: '12px' }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: '#818cf8' }} />
                <span>Copilot respondendo…</span>
              </div>
            )}
          </div>

          {/* Action footer */}
          {!taskCompleted && (
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              {/* User input */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
              }}>
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTaskReply(); } }}
                  placeholder="Responda ao Copilot ou forneça mais informações…"
                  disabled={taskLoading}
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    fontSize: '13px', color: 'rgba(255,255,255,0.85)',
                  }}
                />
                <button
                  onClick={handleTaskReply}
                  disabled={!userInput.trim() || taskLoading}
                  style={{
                    padding: '6px 8px',
                    background: userInput.trim() ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: 'none', borderRadius: '6px', cursor: userInput.trim() ? 'pointer' : 'not-allowed',
                    color: userInput.trim() ? '#818cf8' : 'rgba(255,255,255,0.2)',
                    transition: 'all 0.15s',
                  }}
                >
                  <Send size={16} />
                </button>
              </div>

              {/* Complete task button */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  id="btn-copilot-complete-task"
                  onClick={handleCompleteTask}
                  disabled={taskLoading}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '9px',
                    background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.08))',
                    border: '1px solid rgba(16,185,129,0.25)',
                    borderRadius: '8px',
                    color: '#34d399', fontSize: '12px', fontWeight: 700,
                    cursor: taskLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    opacity: taskLoading ? 0.6 : 1,
                  }}
                >
                  <CheckCircle2 size={14} />
                  Marcar como Concluída
                </button>
                <button
                  id="btn-copilot-go-tasks"
                  onClick={() => { handleClose(); window.location.hash = '#/tasks'; }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '9px 14px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <ArrowRight size={13} />
                  Ver Tarefas
                </button>
              </div>
            </div>
          )}

          {taskCompleted && (
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              color: '#34d399', fontSize: '13px', fontWeight: 600,
            }}>
              <CheckCircle2 size={16} />
              Tarefa concluída com sucesso!
              <button onClick={handleClose} style={{
                marginLeft: '8px', padding: '4px 12px',
                background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)',
                borderRadius: '6px', color: '#34d399', fontSize: '11px', cursor: 'pointer',
              }}>
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── GENERAL MODE (original flow) ──────────────────────────────────────────
  return (
    <div style={panelStyle} onClick={handleClose}>
      <div
        style={{ ...innerStyle, maxWidth: '600px', maxHeight: '70vh', minHeight: '300px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#818cf8' }}>
            <Bot size={20} />
            <span style={{ fontWeight: 700, fontSize: '15px', color: '#f3f4f6' }}>CRM Copilot</span>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}>
            <X size={20} />
          </button>
        </div>

        <div ref={scrollRef} style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px' }}>
          {generalThread.map((message, index) => message.role === 'user'
            ? <UserBubble key={`${message.role}-${index}`} msg={message} />
            : <ReplyBubble key={`${message.role}-${index}`} msg={message} />)}

          {generalLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'rgba(255,255,255,0.4)' }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px', color: '#818cf8' }} />
              <span>Pensando...</span>
            </div>
          )}

          {!generalLoading && generalThread.length === 0 && !generalResponse && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.35)', gap: '10px' }}>
              <Bot size={44} />
              <p style={{ margin: 0, fontWeight: 500 }}>Como posso ajudar você hoje?</p>
              <p style={{ margin: 0, fontSize: '12px', textAlign: 'center' }}>Ex: "Criar uma tarefa para amanhã" ou "Mudar negócio X para negociação"</p>
            </div>
          )}

          {!generalLoading && generalResponse?.type === 'tool_call' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                padding: '16px',
                background: 'rgba(99,102,241,0.06)',
                border: '1px solid rgba(99,102,241,0.25)',
                borderRadius: '12px',
                display: 'flex', flexDirection: 'column', gap: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: '#818cf8', fontSize: '13px' }}>
                  <Check size={16} />
                  Pedido de Confirmação
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                  O Copilot quer executar: <strong style={{ background: 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: '4px' }}>{generalResponse.intent}</strong>
                </p>
                <pre style={{ margin: 0, fontSize: '11px', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', overflowX: 'auto', color: 'rgba(255,255,255,0.7)' }}>
                  {JSON.stringify(generalResponse.arguments, null, 2)}
                </pre>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button onClick={() => setGeneralResponse(null)} style={{ padding: '7px 14px', background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '7px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '12px' }}>
                    Cancelar
                  </button>
                  <button onClick={handleExecute} style={{ padding: '7px 14px', background: '#6366f1', border: 'none', borderRadius: '7px', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={14} /> Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          {!generalLoading && generalResponse?.type === 'success' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', color: '#34d399', fontSize: '13px' }}>
              <Check size={18} />
              <span>{generalResponse.content}</span>
            </div>
          )}

          {!generalLoading && generalResponse?.type === 'error' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#f87171', fontSize: '13px' }}>
              <XCircle size={18} />
              <span>{generalResponse.content}</span>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <form
            onSubmit={(e) => { e.preventDefault(); handleGeneralSend(); }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '8px 12px' }}
          >
            <input
              type="text"
              value={generalMessage}
              onChange={(e) => setGeneralMessage(e.target.value)}
              placeholder="Digite o que deseja fazer..."
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
              disabled={generalLoading}
              autoFocus
            />
            <button
              type="submit"
              disabled={!generalMessage.trim() || generalLoading}
              style={{ background: 'none', border: 'none', cursor: generalMessage.trim() ? 'pointer' : 'not-allowed', color: generalMessage.trim() ? '#818cf8' : 'rgba(255,255,255,0.2)', padding: '4px' }}
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
